<template>
    <div class="filters-wrapper">
        <ul class="filters-active">
            <template v-for="(filterGroup, filterIndex) in filtersActive">
                <li
                    v-if="filterGroup.length > 1"
                    :key="`$multi-${filterIndex}`"
                    class="list-element"
                >
                    <button
                        :ref="`button-${filterIndex}`"
                        :class="{ 'lower-index': !listOpen[filterIndex] }"
                        class="filters-active__button btn btn--small"
                        @click="toggleList(filterIndex)"
                    >
                        <span
                            class="filters-active__label"
                            v-text="
                                `${filterGroup[0].filterName} (${filterGroup.length})`
                            "
                        />
                        <span class="filters-active__arrow">
                            <SortIcon
                                width="14"
                                height="9"
                                :class="{
                                    'icon--rotate-180': listOpen[filterIndex]
                                }"
                            />
                        </span>
                    </button>
                    <div
                        v-if="listOpen[filterIndex]"
                        :ref="`list-${filterIndex}`"
                        class="filters-active__list"
                        :class="{ 'is-visible': listOpen[filterIndex] }"
                        tabindex="0"
                        role="listbox"
                        @keydown="(event) => onKeyDown(event, filterIndex)"
                    >
                        <button
                            v-for="(filter, index) in filterGroup"
                            :key="`${filterIndex}-${index}`"
                            class="filters-active__list-item btn--clear"
                            @click.prevent="
                                removeFilter({
                                    key: filterIndex,
                                    values: [filter.value]
                                })
                            "
                        >
                            <CheckboxIcon class="checkbox-close-icon" />
                            <span
                                class="filter-list-label"
                                v-text="filter.label || filter.filter_name"
                            ></span>
                        </button>
                    </div>
                </li>
                <li v-else :key="`$single-${filterIndex}`">
                    <button
                        class="filters-active__button btn btn--small"
                        @click.prevent="
                            handleRemoveFilter(
                                filterIndex,
                                filterGroup[0].value
                            )
                        "
                    >
                        {{ filterGroup[0].label || filterGroup[0].filter_name }}
                        <span class="sr-only">Usuń filtr</span>
                        <CloseIcon width="20" aria-hidden="true" />
                    </button>
                </li>
            </template>
            <li v-if="hasActiveFilters && !isMobile" class="list-clear">
                <button
                    class="filters-active__button filters-active__clear btn btn--small"
                    href="#"
                    @click.prevent="cleanFilters"
                >
                    {{ i18n('Clear filters') }}
                    <span class="sr-only">Usuń filtr</span>
                    <CloseIcon width="20" aria-hidden="true" />
                </button>
            </li>
        </ul>

        <li v-if="hasActiveFilters && isMobile" class="list-clear">
            <button
                class="filters-active__button filters-active__clear btn btn--small"
                href="#"
                @click.prevent="cleanFilters"
            >
                {{ i18n('Clear filters') }}
                <CloseIcon width="20" />
            </button>
        </li>
    </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import CloseIcon from '@icons/close-thin.svg'
import CheckboxIcon from '@icons/checkbox-thin.svg'
import SortIcon from '@icons/sort.svg'

export default {
    components: {
        SortIcon,
        CheckboxIcon,
        CloseIcon
    },
    props: {
        filtersActive: {
            type: Object,
            default: () => ({})
        },
        isMobile: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            listOpen: {}
        }
    },
    computed: {
        ...mapGetters(['i18n']),
        hasActiveFilters() {
            return Object.keys(this.filtersActive).length
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        ...mapActions(['removeFilter', 'cleanFilters']),
        toggleList(filterIndex) {
            if (this.isMobile) {
                this.$emit('chooseMobileFilter', filterIndex)

                return
            }

            this.$set(this.listOpen, filterIndex, !this.listOpen[filterIndex])
        },
        handleClickOutside(event) {
            Object.keys(this.listOpen).forEach((filterIndex) => {
                const buttonRef = this.$refs[`button-${filterIndex}`][0]
                const listRef = this.$refs[`list-${filterIndex}`][0]
                if (
                    this.listOpen[filterIndex] &&
                    !buttonRef.contains(event.target) &&
                    !listRef.contains(event.target)
                ) {
                    this.$set(this.listOpen, filterIndex, false)
                }
            })
        },
        handleRemoveFilter(filterIndex, filterValue) {
            this.removeFilter({
                key: filterIndex,
                values: [filterValue]
            })
        },
        onKeyDown(event, filterIndex) {
            if (event.code === 'Escape') {
                this.$set(this.listOpen, filterIndex, false)
            }
        }
    }
}
</script>
<style scoped lang="scss">
.filters-wrapper {
    display: flex;
    @media #{$media-to-sm} {
        overflow: hidden;
    }
}

li {
    position: relative;
}

.filters-active {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;

    @media #{$media-to-sm} {
        order: 2;
        padding-bottom: 8px;
        overflow-x: auto;
        overflow-y: hidden;
        flex-wrap: nowrap;
        gap: 6px;
        &::-webkit-scrollbar {
            height: 4px;
        }
        &::-webkit-scrollbar-track {
            background: #cee7d8;
            border-radius: 8px;
        }
        &::-webkit-scrollbar-thumb {
            background: #007f32;
            border-radius: 8px;
        }
    }

    .checkbox-close-icon {
        height: 22px;
        path {
            color: #595a68;
        }
    }

    .close-icon {
        scale: 0.8;
        height: 15px;
        path {
            color: #595a68;
        }
    }
    &__button {
        display: flex;
        position: relative;
        background-color: #fff;
        border-radius: 25px;
        height: 40px;
        z-index: 6;
        gap: 5px;
        padding: 0 10px;
        &.lower-index {
            z-index: 2;
        }
        @media #{$media-to-sm} {
            height: 26px;
            min-height: 26px;
            font-size: 12px;
            gap: 6px;
        }
        &:hover,
        &:focus {
            .filters-active__arrow {
                svg {
                    path {
                        fill: white;
                    }
                }
            }
            .close-icon {
                path {
                    fill: white;
                }
            }
        }
    }

    &__clear {
        background-color: transparent;
        border: 0;
        border-radius: 0px;
        &:hover,
        &:focus {
            background-color: transparent;
            color: $blue-dianne;
        }
        z-index: 1;
        @media #{$media-to-sm} {
            padding-left: 0;
            order: 1;
        }
        .close-icon {
            color: #007f32;
        }
    }

    &__list {
        max-height: 450px;
        overflow-y: auto;
        overflow-x: hidden;
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: #fff;
        margin-top: 0;
        padding-top: 0;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
        z-index: 10;
        display: block;
        width: fit-content;
        min-width: calc(100% + 10px);
        box-shadow: 0 30px 40px 0 rgba(47, 49, 70, 0.1);

        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
        }

        &::-webkit-scrollbar-thumb {
            background: #007f32;
            border-radius: 10px;
        }
    }

    &__arrow {
        svg {
            @media #{$media-to-sm} {
                width: 9px;
                height: 6px;
            }

            path {
                fill: $sherpa-blue;
            }
        }
    }

    &__list-item {
        display: flex;
        justify-content: left;
        padding: 10px 16px;
        text-align: center;
        cursor: pointer;
        white-space: normal;
        color: #000;
        align-items: center;
        gap: 8px;
        width: calc(100% + 20px);

        .filter-list-label {
            max-width: 80%;
            text-align: left;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 22px;
            white-space: wrap;
        }

        &.is-active,
        &:hover {
            background-color: $sherpa-blue-light;
        }
        .close-icon {
            margin-top: -3px;
        }
    }
}

.list-clear {
    @media #{$media-to-sm} {
        order: 1;
    }
}
</style>
